import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import {
  ButtonPrimary,
  ButtonSecondary,
  HStack,
  VStack,
} from "../../../components/utils";
import { FileDropzone } from "../../../components/shared/FileDropzone";
import convertToBase64 from "../../../utils/convertToBase64";
import {
  convertDocxToHtml,
  generateEmailBodyFromPrompt,
  getCampaignWrapper,
  getSavedCampaignTemplates,
  SavedCampaignTemplate,
} from "../../../api/customEmail";
import optionsApi from "../../../api/optionsApi";

interface Props {
  onNext: (html: string, subject?: string) => void;
}

export default function Step1Upload({ onNext }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState<SavedCampaignTemplate[]>(
    []
  );
  const [selectedSavedId, setSelectedSavedId] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState<"upload" | "saved" | "ai">(
    "upload"
  );

  // Preview dialog state
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    getSavedCampaignTemplates()
      .then(setSavedTemplates)
      .catch(() => {});
  }, []);

  const wrapInHissaTemplate = async (bodyHtml: string): Promise<string> => {
    const wrapper = await getCampaignWrapper();
    const BODY_START = "<!-- body start -->";
    const BODY_END = "<!-- body end -->";
    const startIdx = wrapper.indexOf(BODY_START);
    const endIdx = wrapper.indexOf(BODY_END);
    if (startIdx === -1 || endIdx === -1) return bodyHtml;
    // The white content table is part of the wrapper structure (outside the markers).
    // Just inject the raw body HTML between the markers — no extra wrapping needed.
    return `${wrapper.substring(
      0,
      startIdx + BODY_START.length
    )}${bodyHtml}${wrapper.substring(endIdx)}`;
  };

  const handleContinue = useCallback(async () => {
    if (!file) {
      toast.error("Please select a file to upload");
      return;
    }
    setLoading(true);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase();
      if (ext === "html" || ext === "htm") {
        const text = await file.text();
        if (text.includes("<!-- body start -->")) {
          onNext(text);
        } else {
          onNext(await wrapInHissaTemplate(text));
        }
      } else if (ext === "docx") {
        const base64 = (await convertToBase64(file)) as string;
        const html = await convertDocxToHtml(base64);
        const decoded = atob(html);
        onNext(await wrapInHissaTemplate(decoded));
      } else {
        toast.error("Only .docx and .html files are supported");
      }
    } catch {
      toast.error("Failed to process the file. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [file, onNext]);

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Please describe the email you want to generate");
      return;
    }
    setAiGenerating(true);
    try {
      const body = await generateEmailBodyFromPrompt(aiPrompt);
      const wrapped = await wrapInHissaTemplate(body);
      // Show preview dialog instead of directly proceeding
      setPreviewHtml(wrapped);
      setShowPreviewDialog(true);
    } catch (error: any) {
      // Handle rate limit errors
      if (error?.response?.status === 429) {
        const retryAfter = error.response.data?.error?.retryAfter;
        const hourlyRemaining = error.response.data?.error?.hourlyRemaining;
        if (retryAfter === 3600) {
          toast.error(
            `AI generation limit reached. You can generate 10 emails per hour. Please try again in ${Math.ceil(
              retryAfter / 60
            )} minutes.`
          );
        } else {
          toast.error(
            "Company AI generation limit reached. Please try again tomorrow."
          );
        }
      } else if (error?.response?.data?.message) {
        // Show specific error message from backend (e.g., ESOP relevance check)
        toast.error(error.response.data.message);
      } else {
        toast.error("AI generation failed. Please try again.");
      }
    } finally {
      setAiGenerating(false);
    }
  };

  const handleRegenerateAI = async () => {
    if (!aiPrompt.trim()) return;
    setRegenerating(true);
    try {
      const body = await generateEmailBodyFromPrompt(aiPrompt);
      const wrapped = await wrapInHissaTemplate(body);
      setPreviewHtml(wrapped);
      toast.success("Email regenerated successfully");
    } catch (error: any) {
      if (error?.response?.status === 429) {
        toast.error("Rate limit reached. Please wait before regenerating.");
        setShowPreviewDialog(false);
      } else if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Regeneration failed. Please try again.");
      }
    } finally {
      setRegenerating(false);
    }
  };

  const handleAcceptPreview = () => {
    setShowPreviewDialog(false);
    onNext(previewHtml);
  };

  const handleCancelPreview = () => {
    setShowPreviewDialog(false);
    setPreviewHtml("");
  };

  const handleLoadSavedTemplate = useCallback(async () => {
    if (!selectedSavedId) return;
    setLoading(true);
    try {
      const res = await optionsApi.get(
        `v1/template/custom-campaigns/${selectedSavedId}/content`
      );
      const saved = savedTemplates.find((t) => t.id === selectedSavedId);
      onNext(res.data.data.html as string, saved?.subject);
    } catch {
      toast.error(
        "Failed to load saved template. Please try uploading a file."
      );
    } finally {
      setLoading(false);
    }
  }, [selectedSavedId, onNext, savedTemplates]);

  const tabs = [
    {
      key: "upload" as const,
      label: "Upload File",
      icon: "mdi:upload-outline",
    },
    ...(savedTemplates.length > 0
      ? [
          {
            key: "saved" as const,
            label: "Saved Templates",
            icon: "mdi:bookmark-outline",
          },
        ]
      : []),
    { key: "ai" as const, label: "Generate with AI", icon: "mdi:auto-fix" },
  ];

  return (
    <VStack className="gap-0 h-full">
      {/* Tab bar */}
      <div className="flex border-b border-gray-100 px-6 pt-5 shrink-0">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors mr-1 ${
              activeTab === tab.key
                ? "border-orange-501 text-orange-501"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <Icon icon={tab.icon} height={15} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <VStack className="flex-1 p-6 gap-5 overflow-y-auto">
        {activeTab === "upload" && (
          <>
            <p className="text-sm text-gray-500">
              Upload a <span className="font-medium text-gray-700">.docx</span>{" "}
              or <span className="font-medium text-gray-700">.html</span> file.
              Use{" "}
              <code className="bg-gray-100 px-1 rounded text-xs">
                #variableName#
              </code>{" "}
              for placeholders.
            </p>
            <FileDropzone
              onFileSelect={(f) => {
                setFile(f);
                setSelectedSavedId("");
              }}
              onClear={() => setFile(null)}
              selectedFile={file ?? undefined}
              acceptedFileExtensions={[".docx", ".html", ".htm"]}
              instructionText=".docx or .html files only"
            />
            <HStack className="flex-row-reverse mt-auto">
              <ButtonPrimary
                onClick={handleContinue}
                loading={loading}
                disabled={!file}
              >
                Continue
              </ButtonPrimary>
            </HStack>
          </>
        )}

        {activeTab === "saved" && (
          <>
            <HStack className="items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <Icon
                icon="mdi:information-outline"
                className="text-blue-500 shrink-0 mt-0.5"
                height={16}
              />
              <p className="text-sm text-blue-700">
                Select a template from the list below and click{" "}
                <span className="font-semibold">Load Template</span> to reuse or
                edit it.
              </p>
            </HStack>
            <VStack className="gap-2">
              {savedTemplates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setSelectedSavedId(t.id)}
                  className={`flex items-center justify-between w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                    selectedSavedId === t.id
                      ? "border-orange-400 bg-orange-50"
                      : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      {t.templateName}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Subject: {t.subject}
                    </p>
                  </div>
                  {selectedSavedId === t.id && (
                    <Icon
                      icon="mdi:check-circle"
                      className="text-orange-500 shrink-0"
                      height={18}
                    />
                  )}
                </button>
              ))}
            </VStack>
            <HStack className="flex-row-reverse mt-auto">
              <ButtonPrimary
                onClick={handleLoadSavedTemplate}
                loading={loading}
                disabled={!selectedSavedId}
              >
                Load Template
              </ButtonPrimary>
            </HStack>
          </>
        )}

        {activeTab === "ai" && (
          <>
            <div className="flex items-start gap-3 p-4 bg-violet-50 border border-violet-100 rounded-lg">
              <Icon
                icon="mdi:information-outline"
                className="text-violet-500 shrink-0 mt-0.5"
                height={16}
              />
              <p className="text-xs text-violet-700 leading-relaxed">
                Describe the email and AI will write the body. Use{" "}
                <code className="bg-violet-100 px-1 rounded">
                  #employee.name#
                </code>
                ,{" "}
                <code className="bg-violet-100 px-1 rounded">
                  #company.name#
                </code>{" "}
                for system data. For custom per-employee data use simple words
                like <code className="bg-violet-100 px-1 rounded">#bonus#</code>{" "}
                (no dots).
              </p>
            </div>
            <textarea
              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300 resize-none"
              rows={8}
              placeholder="e.g. Write a congratulatory email for new ESOP grant recipients. Address them by name, mention the company name, and guide them to log into the Hissa portal to view their grant details and accept the offer."
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
            />
            <HStack className="flex-row-reverse mt-auto">
              <ButtonPrimary
                onClick={handleAIGenerate}
                loading={aiGenerating}
                disabled={!aiPrompt.trim()}
                className="flex items-center gap-1"
              >
                <Icon icon="mdi:auto-fix" height={15} />
                Generate Email
              </ButtonPrimary>
            </HStack>
          </>
        )}
      </VStack>

      {/* AI Preview Dialog */}
      <Dialog
        open={showPreviewDialog}
        onClose={handleCancelPreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="flex items-center justify-between border-b">
          <span className="text-lg font-semibold text-gray-800">
            Preview AI Generated Email
          </span>
          <IconButton onClick={handleCancelPreview} size="small">
            <Icon icon="mdi:close" height={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent className="p-0">
          <div className="p-4 space-y-4">
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <Icon
                icon="mdi:information-outline"
                className="text-blue-500 shrink-0 mt-0.5"
                height={16}
              />
              <p className="text-xs text-blue-700">
                Review the AI-generated email below. You can regenerate for a
                different version or accept to continue editing.
              </p>
            </div>

            {/* Preview iframe */}
            <div
              className="border border-gray-200 rounded-lg overflow-hidden"
              style={{ height: "400px" }}
            >
              <iframe
                srcDoc={previewHtml}
                title="Email Preview"
                className="w-full h-full"
                sandbox="allow-same-origin"
              />
            </div>

            {/* Action buttons */}
            <HStack className="justify-between items-center gap-2 pt-2">
              <ButtonSecondary onClick={handleCancelPreview}>
                Cancel
              </ButtonSecondary>
              <HStack className="gap-2">
                <ButtonSecondary
                  onClick={handleRegenerateAI}
                  loading={regenerating}
                  disabled={regenerating}
                  className="flex items-center gap-1"
                >
                  <Icon icon="mdi:refresh" height={16} />
                  {regenerating ? "Regenerating..." : "Regenerate"}
                </ButtonSecondary>
                <ButtonPrimary
                  onClick={handleAcceptPreview}
                  disabled={regenerating}
                  className="flex items-center gap-1"
                >
                  <Icon icon="mdi:check" height={16} />
                  {regenerating ? "Regenerating..." : "Accept & Continue"}
                </ButtonPrimary>
              </HStack>
            </HStack>
          </div>
        </DialogContent>
      </Dialog>
    </VStack>
  );
}
